/**
 * Minimal, dependency-free SVG -> EPS (PostScript) converter.
 * Built specifically for the path syntax imagetracerjs emits:
 * absolute M / L / Q / C / Z commands with a single fill color per path.
 * Good enough for print-shop / laser-cutter style vector exports.
 */

function parsePathD(d) {
  const tokens = d.match(/[MLCQZ]|-?\d*\.?\d+(?:e-?\d+)?/gi) || []
  const commands = []
  let i = 0
  while (i < tokens.length) {
    const cmd = tokens[i].toUpperCase()
    if (cmd === 'Z') {
      commands.push({ cmd: 'Z' })
      i += 1
      continue
    }
    const argCount = { M: 2, L: 2, Q: 4, C: 6 }[cmd]
    if (argCount === undefined) {
      i += 1
      continue
    }
    const args = tokens.slice(i + 1, i + 1 + argCount).map(Number)
    commands.push({ cmd, args })
    i += 1 + argCount
  }
  return commands
}

function parseFillColor(pathEl) {
  const raw = pathEl.getAttribute('fill') || pathEl.style.fill || 'rgb(0,0,0)'
  const match = raw.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/i)
  if (match) {
    return [Number(match[1]) / 255, Number(match[2]) / 255, Number(match[3]) / 255]
  }
  const hex = raw.match(/^#([0-9a-f]{6})$/i)
  if (hex) {
    const n = parseInt(hex[1], 16)
    return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255]
  }
  return [0, 0, 0]
}

/**
 * Converts an SVG string (as produced by traceImage.js) into an EPS string.
 * @param {string} svgString
 * @param {number} width
 * @param {number} height
 * @param {{color: string}|null} solidBackground - optional opaque backdrop rect
 */
export function svgToEps(svgString, width, height, solidBackground = null) {
  const parser = new DOMParser()
  const doc = parser.parseFromString(svgString, 'image/svg+xml')
  const paths = Array.from(doc.querySelectorAll('path'))
  const flipY = (y) => (height - y).toFixed(2)

  const lines = []
  lines.push('%!PS-Adobe-3.0 EPSF-3.0')
  lines.push(`%%BoundingBox: 0 0 ${Math.ceil(width)} ${Math.ceil(height)}`)
  lines.push(`%%HiResBoundingBox: 0 0 ${width} ${height}`)
  lines.push('%%Creator: image-to-vector-converter')
  lines.push('%%Pages: 1')
  lines.push('%%EndComments')

  const emitPath = (commandList, [r, g, b]) => {
    lines.push('newpath')
    let cur = { x: 0, y: 0 }
    for (const c of commandList) {
      if (c.cmd === 'M') {
        const [x, y] = c.args
        lines.push(`${x.toFixed(2)} ${flipY(y)} moveto`)
        cur = { x, y }
      } else if (c.cmd === 'L') {
        const [x, y] = c.args
        lines.push(`${x.toFixed(2)} ${flipY(y)} lineto`)
        cur = { x, y }
      } else if (c.cmd === 'Q') {
        const [qx, qy, x, y] = c.args
        const cp1x = cur.x + (2 / 3) * (qx - cur.x)
        const cp1y = cur.y + (2 / 3) * (qy - cur.y)
        const cp2x = x + (2 / 3) * (qx - x)
        const cp2y = y + (2 / 3) * (qy - y)
        lines.push(
          `${cp1x.toFixed(2)} ${flipY(cp1y)} ${cp2x.toFixed(2)} ${flipY(cp2y)} ${x.toFixed(2)} ${flipY(y)} curveto`
        )
        cur = { x, y }
      } else if (c.cmd === 'C') {
        const [x1, y1, x2, y2, x, y] = c.args
        lines.push(
          `${x1.toFixed(2)} ${flipY(y1)} ${x2.toFixed(2)} ${flipY(y2)} ${x.toFixed(2)} ${flipY(y)} curveto`
        )
        cur = { x, y }
      } else if (c.cmd === 'Z') {
        lines.push('closepath')
      }
    }
    lines.push(`${r.toFixed(3)} ${g.toFixed(3)} ${b.toFixed(3)} setrgbcolor`)
    lines.push('fill')
  }

  if (solidBackground) {
    const hex = solidBackground.color.replace('#', '')
    const n = parseInt(hex, 16)
    const r = ((n >> 16) & 255) / 255
    const g = ((n >> 8) & 255) / 255
    const b = (n & 255) / 255
    lines.push('newpath')
    lines.push(`0 0 moveto ${width} 0 lineto ${width} ${height} lineto 0 ${height} lineto closepath`)
    lines.push(`${r.toFixed(3)} ${g.toFixed(3)} ${b.toFixed(3)} setrgbcolor`)
    lines.push('fill')
  }

  for (const pathEl of paths) {
    const d = pathEl.getAttribute('d')
    if (!d) continue
    const commandList = parsePathD(d)
    const color = parseFillColor(pathEl)
    emitPath(commandList, color)
  }

  lines.push('showpage')
  lines.push('%%EOF')
  return lines.join('\n')
}
